import { Immutable } from 'immer';

/**
 * Utility function for generating 'paths' into an object.
 *
 * Useful for typing methods like lodash's get and set.
 *
 * @param selector
 * @returns a path (array) into the object
 */
export function pathSelector<Source extends Pathable, Target>(
  selector: PathSelector<Source, Target>
) {
  const proxy = createPathTrackingProxy<Source>();
  // manual cast because we don't want to pollute the signature of the selector to include the symbol.
  const result = selector(proxy) as PathSelectionWrapperInternal<Target>;
  return result[PathSymbol];
}

function isNumber(p: string) {
  return !isNaN(parseFloat(p));
}

export type Path = (number | string)[];

export type Pathable = object | unknown[];
/**
 * Type for wrapping an existing type and ensuring that it is only treated as an AST (no setting or calling of properties or sub-properties)
 */
//TODO(nibeauli): this needs to be more restrictive and erase the constructor & invocation signatures in addition to the write-ability of the properties.
export type PathSelectionWrapper<Source extends Pathable> = Immutable<Source>;


// Internal symbol for keeping track of the 'path' into the object
// used to avoid pollution if someone goes and uses something clever like Object.keys
const PathSymbol = Symbol('path');
type PathSelectionWrapperInternal<Source> = Source extends Pathable
  ? PathSelectionWrapper<Source> & { [PathSymbol]: Path }
  : never;

function createPathTrackingProxy<Source extends Pathable>(
  basePath: (number | string)[] = []
): PathSelectionWrapperInternal<Source> {
  return new Proxy<PathSelectionWrapperInternal<Source>>(
    {} as PathSelectionWrapperInternal<Source>,
    {
      get(_, prop) {
        if (prop === PathSymbol) {
          return basePath;
        } else if (typeof prop === 'symbol') {
          //TODO(nibeauli): if this is needed we may need to think about how to handle it w/o exposing our symbol magic.
          throw new Error('unable to path with symbols');
        } else if (isNumber(prop)) {
          // We don't necessarily know or need to know the intermediate path "types", we'll let typescript handle that in a higher abstraction level.
          return createPathTrackingProxy<Pathable>(
            basePath.concat(parseFloat(prop))
          );
        } else {
          // We don't necessarily know or need to know the intermediate path "types", we'll let typescript handle that in a higher abstraction level.
          return createPathTrackingProxy<Pathable>(basePath.concat(prop));
        }
      },
      apply() {
        // ideally the type system would erase the callable but that seems to cause issues with primitives like
        // string no longer being valid strings. So we'll just throw an error here that should be caught by the
        // developer during development. If this makes it to runtime it means the path selector code was never called.
        throw new Error('Cannot call a function when during path selection.');
      },
    }
  );
}

export type PathSelector<From extends Pathable, To> = (
  o: PathSelectionWrapper<From>
) => To extends Pathable ? PathSelectionWrapper<To> : To;

export type PathsSelectorFrom<T extends Pathable> = (
  o: PathSelectionWrapper<T>
) => unknown[];

export type PathsSelectorTo<T extends unknown[]> = (
  o: PathSelectionWrapper<Pathable>
) => T[number] extends Pathable ? PathSelectionWrapper<T[number]>[] : T[number];

/**
 * Utility for defining a collection of Paths
 * @param selector
 * @returns
 */
export function pathsSelector<T extends Pathable>(
  selector: (o: PathSelectionWrapper<T>) => unknown[]
) {
  const proxy = createPathTrackingProxy<T>();

  // manual cast because we don't want to pollute the signature of the selector to include the symbol.
  const results = selector(proxy) as PathSelectionWrapperInternal<Pathable>[];
  const paths = results.map((r) => r[PathSymbol]);
  return paths;
}
